<script setup>
import FormGroup from '@maison/components/form/FormGroup.vue';
import { PhxStrapFinderPicture } from '@phx-styleguides/iwc';
import { computed, inject } from 'vue';
import { emits as defaultEmits, props as defaultProps, useSection } from '@maison/composables/strapFinder/useSection';
import RadioListField from '@maison/components/form/fields/RadioListField.vue';
import ConfiguratorSectionLayout from '@maison/components/strapFinder/sections/ConfiguratorSectionLayout.vue';

const props = defineProps({
  ...defaultProps,
  currentBuckleType: {
    type: String,
    default: null,
  },
  currentStrapStyles: {
    type: Array,
    default: () => [],
  },
});

defineEmits(defaultEmits);

const { id, preTitle, nextSectionId } = useSection('strap');

const currentStrap = defineModel('currentStrap', {
  type: String,
  required: true,
});

const straps = inject('straps', []);

const strapField = computed(() => {
  // We need both to ensure we can display the correct straps
  if (!props.currentBuckleType || !props.currentStrapStyles.length) return null;

  const filteredStraps = straps.value.filter(
    ({ buckleType, type, color, isSteelStrap }) =>
      (isSteelStrap || buckleType.code === props.currentBuckleType) &&
      props.currentStrapStyles.includes(`${type.code}.${color.code}`),
  );

  return {
    id: id.value,
    name: id.value,
    label: '',
    options: filteredStraps.map(({ modelReference, name, image, sizes }) => {
      // We are getting the price of the first size available for the strap
      const firstSizeKey = Object.keys(sizes)[0];
      const firstSize = sizes[firstSizeKey];
      return {
        id: modelReference,
        value: modelReference,
        label: name,
        media: {
          id: modelReference,
          captionText: name,
          ...image,
        },
        image,
        additionalContent: firstSize.priceWithCurrency,
      };
    }),
  };
});
</script>

<template>
  <configurator-section-layout
    :id="id"
    :title="title"
    :description="description"
    :memo="memo"
    :pre-title="preTitle"
    :next-section-id="nextSectionId"
  >
    <template #media>
      <phx-strap-finder-picture v-for="strap in strapField?.options" :key="strap.id" v-bind="strap.media" />
    </template>

    <template #default="{ onShowMedia }">
      <form @submit.prevent.stop @change="$emit('onStepValidate', $event, id, onShowMedia)">
        <form-group
          v-if="strapField"
          :attrs="strapField"
          :is-fieldset="true"
          custom-fieldset-classes="grid md:grid-cols-2 gap-4"
        >
          <radio-list-field v-model="currentStrap" type="rich" />
        </form-group>
      </form>
    </template>
  </configurator-section-layout>
</template>
